import type { Dispatch, MouseEvent, SetStateAction } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CheckIcon } from '@heroicons/react/24/outline';

import { updateGoogleConsent } from '~/components/Pixels/googleConsentMode';
import type { CookieValues, SetCookieOptions } from '~/hooks/useUtils/useCookies';
import type { Publication } from '~/interfaces/publication';

interface CustomSelectProps {
  value: string;
  selectedCookies: string[];
  setSelectedCookies: Dispatch<SetStateAction<string[]>>;
  disabled: boolean;
}

interface Props {
  publication: Pick<Publication, 'enable_cookie_banner'>;
  cookies: CookieValues;
  setCookie: (options: SetCookieOptions) => void;
  ga4ConsentModeEnabled: boolean;
}

const CustomSelect = ({ value, selectedCookies, setSelectedCookies, disabled }: CustomSelectProps) => {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      onClick={() => {
        if (selectedCookies.includes(value)) {
          setSelectedCookies(selectedCookies.filter((v) => v !== value));
        } else {
          setSelectedCookies([...selectedCookies, value]);
        }
      }}
      className={`flex flex-row space-x-2 ${disabled && 'opacity-40'}`}
      disabled={disabled}
    >
      <div
        className={`rounded border border-white p-0.5 ${
          !selectedCookies.includes(value) && !disabled ? 'hover:bg-gray-800' : 'bg-white'
        }`}
      >
        <CheckIcon className={'h-4 w-4 text-black'} />
      </div>
      <p className="my-auto text-sm font-semibold text-white">
        {t(`cookie_banner.cookies.${value.toLowerCase().replace(/ /g, '_')}`)}
      </p>
    </button>
  );
};

const BeehiivHoney = ({ publication, cookies, setCookie, ga4ConsentModeEnabled }: Props) => {
  const { t } = useTranslation();
  const [beehiivHoney, setbeehiivHoney] = useState<boolean>(false);
  const [selectedCookies, setSelectedCookies] = useState<string[]>([]);
  const [customizeEnabled, setCustomizeEnabled] = useState<boolean>(false);
  const beehiivHoneyRef = useRef<HTMLInputElement>(null);

  const persistCookieConsent = (consent: { marketing: boolean; dataProcessing: boolean }) => {
    setCookie({
      name: 'beehiiv_honey',
      value: JSON.stringify(consent),
      days: 1000,
    });

    if (ga4ConsentModeEnabled) {
      updateGoogleConsent(consent.marketing);
    }

    setbeehiivHoney(false);
  };

  useEffect(() => {
    const shouldShowBanner = publication.enable_cookie_banner && !cookies.beehiiv_honey;
    setbeehiivHoney(shouldShowBanner);

    const timer = setTimeout(() => {
      if (shouldShowBanner && beehiivHoneyRef.current) {
        beehiivHoneyRef.current.classList.add('!opacity-100', '!translate-y-[0%]');
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [cookies, publication.enable_cookie_banner]);

  const onClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const value = e.currentTarget.value;
    const beehiivHoneyValues = {
      marketing: false,
      dataProcessing: false,
    };

    if (value === 'customize') {
      setCustomizeEnabled(true);
    }

    if (value === 'back') {
      setCustomizeEnabled(false);
    }

    if (value === 'decline') {
      beehiivHoneyValues.marketing = false;
      beehiivHoneyValues.dataProcessing = false;
      persistCookieConsent(beehiivHoneyValues);
    }

    if (value === 'accept') {
      beehiivHoneyValues.marketing = true;
      beehiivHoneyValues.dataProcessing = true;
      persistCookieConsent(beehiivHoneyValues);
    }

    if (value === 'allow-selection') {
      beehiivHoneyValues.marketing = selectedCookies.includes('Marketing Cookies');
      beehiivHoneyValues.dataProcessing = selectedCookies.includes('Data Processor Cookies');
      persistCookieConsent(beehiivHoneyValues);
    }
  };

  if (!beehiivHoney) {
    return null;
  }

  return (
    <div
      ref={beehiivHoneyRef}
      className="fixed bottom-0 left-0 z-50 w-full -translate-y-[100%] bg-black p-4 opacity-0 transition-all duration-500 md:bottom-4 md:left-4 md:w-96 md:rounded-lg"
    >
      {customizeEnabled ? (
        <div className="flex w-full flex-col space-y-1">
          <p className="text-lg font-bold text-white">{t('cookie_banner.customize_title')}</p>
          <p className="text-sm text-white">
            {t('cookie_banner.customize_description')}{' '}
            <a
              href="https://www.beehiiv.com/tou"
              target="_blank"
              className="underline underline-offset-1 hover:opacity-80"
              rel="noreferrer"
            >
              {t('footer.terms_of_use')}
            </a>{' '}
            {t('cookie_banner.for_more_info')}.
          </p>
          <div className="mt-4 flex flex-col space-y-3">
            <CustomSelect
              value="Necessary Cookies"
              selectedCookies={selectedCookies}
              setSelectedCookies={setSelectedCookies}
              disabled={true}
            />
            <CustomSelect
              value="Marketing Cookies"
              selectedCookies={selectedCookies}
              setSelectedCookies={setSelectedCookies}
              disabled={false}
            />
            <CustomSelect
              value="Data Processor Cookies"
              selectedCookies={selectedCookies}
              setSelectedCookies={setSelectedCookies}
              disabled={false}
            />
          </div>
        </div>
      ) : (
        <div className="flex w-full flex-col space-y-1">
          <p className="font-bold text-white">{t('cookie_banner.title')}</p>
          <p className="text-sm text-white">
            {t('cookie_banner.read')}{' '}
            <a
              href="https://www.beehiiv.com/privacy"
              target="_blank"
              className="underline underline-offset-1 hover:opacity-80"
              rel="noreferrer"
            >
              {t('footer.privacy_policy')}
            </a>{' '}
            {t('shared.cta.and')}{' '}
            <a
              href="https://www.beehiiv.com/tou"
              target="_blank"
              className="underline underline-offset-1 hover:opacity-80"
              rel="noreferrer"
            >
              {t('footer.terms_of_use')}
            </a>{' '}
            {t('cookie_banner.for_more_info')}.
          </p>
        </div>
      )}
      {customizeEnabled ? (
        <div className="mt-3 flex gap-1.5">
          <button
            type="button"
            onClick={onClick}
            value="back"
            className="flex-1 rounded-sm border border-white p-2 px-4 text-sm text-white"
          >
            {t('cookie_banner.actions.back')}
          </button>
          <button
            type="button"
            value="allow-selection"
            className="flex-1 rounded-sm border border-white p-2 px-4 text-sm text-white"
            onClick={onClick}
          >
            {t('cookie_banner.actions.allow_selection')}
          </button>
        </div>
      ) : (
        <div className="mt-3 flex gap-1.5">
          <button
            type="button"
            className="flex-1 rounded-sm border border-white p-2 px-4 text-sm text-white"
            onClick={onClick}
            value="accept"
          >
            {t('cookie_banner.actions.accept')}
          </button>
          <button
            type="button"
            value="customize"
            className="flex-1 rounded-sm border border-white p-2 px-4 text-sm text-white"
            onClick={onClick}
          >
            {t('cookie_banner.actions.customize')}
          </button>
          <button
            type="button"
            className="flex-1 rounded-sm border border-white p-2 px-4 text-sm text-white"
            onClick={onClick}
            value="decline"
          >
            {t('cookie_banner.actions.decline')}
          </button>
        </div>
      )}
    </div>
  );
};

export default BeehiivHoney;
